import { yupResolver } from '@hookform/resolvers/yup';
import { DividerWithText } from '@mindpal-co/mindpal-ui';
import { useMutation } from '@tanstack/react-query';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { Controller } from 'react-hook-form';
import type { InferType } from 'yup';
import { boolean, object, string } from 'yup';

import AuthPageLayout from '@/components/Auth/AuthPageLayout';
import ButtonGoogleAuth from '@/components/Auth/ButtonGoogleAuth';
import ButtonLinkedInAuth from '@/components/Auth/ButtonLinkedInAuth';
import Button from '@/components/Button';
import Checkbox from '@/components/Form/Fields/Checkbox';
import PhoneInputField from '@/components/Form/Fields/PhoneInputField';
import TextInputField from '@/components/Form/Fields/TextInputField';
import Text from '@/components/Text';
import useForm from '@/hooks/useForm';
import { useRedirectAuthUsers } from '@/hooks/useRedirectAuthUsers';
import { register as apiRegister } from '@/services/auth/auth';
import { getStringParam } from '@/utils/queryParams';

const SignUp = () => {
  const router = useRouter();
  const { callback } = router.query;
  const parsedCallback = getStringParam(callback);

  useRedirectAuthUsers();
  const { mutate } = useMutation({
    mutationFn: apiRegister,
    mutationKey: ['register'],
    onSuccess: () => router.push('/dashboard/auth/register/activate'),
  });
  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: '',
      email: '',
      phone: '',
      linkedinUrl: '',
      password: '',
      passwordRepeated: '',
      privacyPolicy: false,
      smsAccountConsent: false,
      smsMarketingConsent: false,
    },
    resolver: yupResolver(schema),
  });
  return (
    <AuthPageLayout>
      <Text
        variant="h2"
        className="mb-8 py-2 pt-8 text-center font-semibold text-neutral-800"
      >
        Sign up to MindPal
      </Text>
      <ButtonGoogleAuth callbackPathname={parsedCallback ?? null}>
        Sign up with Google
      </ButtonGoogleAuth>
      <ButtonLinkedInAuth callbackPathname={parsedCallback ?? null}>
        Sign up with LinkedIn
      </ButtonLinkedInAuth>
      <DividerWithText className="before:border-neutral-400 after:border-neutral-400">
        <span className="text-neutral-400">or</span>
      </DividerWithText>
      <form
        onSubmit={handleSubmit((formData) =>
          mutate({
            ...formData,
            callbackUrl: parsedCallback
              ? `${window.location.origin}${parsedCallback}`
              : null,
          })
        )}
        className="flex flex-col gap-3"
      >
        <TextInputField
          required
          label="Your fullname"
          className="text-black"
          labelClassName="text-black"
          placeholder="name"
          variant="transparent"
          error={errors.name?.message}
          {...register('name')}
        />
        <TextInputField
          required
          label="Your email"
          className="text-black"
          labelClassName="text-black"
          type="email"
          placeholder="name@company.com"
          variant="transparent"
          error={errors.email?.message}
          {...register('email')}
        />
        <Controller
          control={control}
          name="phone"
          render={({ field }) => (
            <PhoneInputField
              required
              labelClassName="text-neutral-800"
              label="Phone number"
              variant="transparent"
              error={errors.phone}
              {...field}
            />
          )}
        />
        <TextInputField
          required
          label="Your LinkedIn"
          className="text-black"
          type="url"
          labelClassName="text-black"
          placeholder="https://www.linkedin.com/in/super-user/"
          variant="transparent"
          error={errors.linkedinUrl?.message}
          {...register('linkedinUrl')}
        />
        <TextInputField
          required
          label="Password"
          className="text-black"
          type="password"
          labelClassName="text-black"
          placeholder="*******************"
          variant="transparent"
          error={errors.password?.message}
          {...register('password')}
        />
        <TextInputField
          required
          label="Re-enter password"
          className="text-black"
          type="password"
          labelClassName="text-black"
          placeholder="*******************"
          variant="transparent"
          error={errors.passwordRepeated?.message}
          {...register('passwordRepeated')}
        />
        <Button className="w-full" type="submit" size="long">
          Continue
        </Button>
        <Link href="/dashboard" tabIndex={-1}>
          <Button variant="quaternary" className="w-full">
            Cancel
          </Button>
        </Link>
        <Checkbox
          required
          className="!text-black"
          error={errors.privacyPolicy}
          {...register('privacyPolicy')}
        >
          <Text variant="body-caption" as="span">
            By signing up, you acknowledge that you have read and understood,
            and agree to{' '}
            <a
              className="text-blue-500 underline"
              href="https://mindpal.co/privacy-policy/"
              target="_blank"
            >
              Terms
            </a>{' '}
            and{' '}
            <a
              className="text-blue-500 underline"
              href="https://mindpal.co/terms-of-service/"
              target="_blank"
            >
              Privacy Policy
            </a>
            .
          </Text>
        </Checkbox>
        <Checkbox
          className="!text-black"
          error={errors.smsAccountConsent}
          {...register('smsAccountConsent')}
        >
          <Text variant="body-caption" as="span">
            I agree to receive customer care and account-related text messages
            from MindPal. Msg freq varies, up to 4 msgs/month. Msg &amp; data
            rates may apply. Reply STOP to opt out, HELP for help. See{' '}
            <a
              className="text-blue-500 underline"
              href="https://mindpal.co/sms-terms/"
              target="_blank"
            >
              SMS Terms
            </a>{' '}
            &amp;{' '}
            <a
              className="text-blue-500 underline"
              href="https://mindpal.co/privacy-policy/"
              target="_blank"
            >
              Privacy Policy
            </a>
            .
          </Text>
        </Checkbox>
        <Checkbox
          className="!text-black"
          error={errors.smsMarketingConsent}
          {...register('smsMarketingConsent')}
        >
          <Text variant="body-caption" as="span">
            I agree to receive marketing text messages from MindPal. Msg freq
            varies, up to 8 msgs/month. Msg &amp; data rates may apply. Reply
            STOP to opt out, HELP for help. See{' '}
            <a
              className="text-blue-500 underline"
              href="https://mindpal.co/sms-terms/"
              target="_blank"
            >
              SMS Terms
            </a>{' '}
            &amp;{' '}
            <a
              className="text-blue-500 underline"
              href="https://mindpal.co/privacy-policy/"
              target="_blank"
            >
              Privacy Policy
            </a>
            .
          </Text>
        </Checkbox>
      </form>
    </AuthPageLayout>
  );
};

export default SignUp;

const schema = object({
  name: string().required('This field is required'),
  email: string()
    .required('This field is required')
    .email('Please enter a valid email'),
  password: string()
    .min(8, 'This field must have a minimum of 8 characters')
    .required('This field is required'),
  passwordRepeated: string()
    .required('This field is required')
    .test('match', 'Passwords do not match', function () {
      return this.parent.password === this.parent.passwordRepeated;
    }),
  phone: string().required('This field is required'),
  linkedinUrl: string()
    .matches(
      /^(https:\/\/)?(www\.)?linkedin\.com\/in\/[a-zA-Z0-9-]{3,30}/,
      'Enter a valid LinkedIn URL starting with "https://www."'
    )
    .required('This field is required'),
  privacyPolicy: boolean().isTrue('This field is required'),
  smsAccountConsent: boolean().default(false),
  smsMarketingConsent: boolean().default(false),
});
export type Register = Omit<
  InferType<typeof schema>,
  'privacyPolicy' | 'surname'
>;
